import config from "@/config";
import { useEffect, useRef } from "react";

declare global {
  interface Window {
    grecaptcha?: {
      render: (
        container: HTMLElement,
        parameters: {
          sitekey: string;
          callback: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
        }
      ) => number;
      reset: (widgetId?: number) => void;
    };
    onLoginRecaptchaLoad?: () => void;
  }
}

const RECAPTCHA_SCRIPT_ID = "google-recaptcha-script";

const safeResetRecaptcha = (widgetId?: number) => {
  if (
    typeof window === "undefined" ||
    !window.grecaptcha ||
    typeof window.grecaptcha.reset !== "function"
  ) {
    return;
  }

  try {
    window.grecaptcha.reset(widgetId);
  } catch {
    // Google throws when reset runs before a client exists; cleanup should be a no-op then.
  }
};

type LoginRecaptchaProps = {
  onChange: (token: string) => void;
  onExpire?: () => void;
  onExpired?: () => void;
};

const LoginRecaptcha = ({
  onChange,
  onExpire,
  onExpired,
}: LoginRecaptchaProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<number | null>(null);
  const onChangeRef = useRef(onChange);
  const onExpiredRef = useRef(onExpired ?? onExpire);
  const siteKey = config.recaptcha_site_key;

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onExpiredRef.current = onExpired ?? onExpire;
  }, [onExpire, onExpired]);

  useEffect(() => {
    if (!siteKey) {
      return;
    }

    const clearContainer = () => {
      if (containerRef.current) {
        containerRef.current.replaceChildren();
      }
    };

    const renderWidget = () => {
      if (
        !containerRef.current ||
        !window.grecaptcha ||
        widgetIdRef.current !== null
      ) {
        return;
      }

      clearContainer();

      const clearToken = () => {
        onChangeRef.current("");
        onExpiredRef.current?.();
      };

      widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token: string) => onChangeRef.current(token),
        "expired-callback": clearToken,
        "error-callback": clearToken,
      });
    };

    const previousOnLoad = window.onLoginRecaptchaLoad;
    const restoreOnLoad = () => {
      window.onLoginRecaptchaLoad = previousOnLoad;
    };

    if (window.grecaptcha) {
      renderWidget();
    } else {
      window.onLoginRecaptchaLoad = () => {
        previousOnLoad?.();
        renderWidget();
      };

      if (!document.getElementById(RECAPTCHA_SCRIPT_ID)) {
        const script = document.createElement("script");
        script.id = RECAPTCHA_SCRIPT_ID;
        script.src =
          "https://www.google.com/recaptcha/api.js?onload=onLoginRecaptchaLoad&render=explicit";
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
      }
    }

    return () => {
      restoreOnLoad();
      if (widgetIdRef.current !== null) {
        safeResetRecaptcha(widgetIdRef.current);
      }
      widgetIdRef.current = null;
      clearContainer();
    };
  }, [siteKey]);

  if (!siteKey) {
    return null;
  }

  return <div ref={containerRef} />;
};

export const resetLoginRecaptcha = () => {
  safeResetRecaptcha();
};

export { LoginRecaptcha };
